// frontend/src/components/visitor/ThemeSelector.tsx
import React, { useState } from 'react';
import { Moon, Sun, Sliders, ChevronDown, X } from 'react-feather';
import { useTheme, ColorTheme } from '../../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';

const ThemeSelector: React.FC = () => {
  const { darkMode, toggleDarkMode, colorTheme, setColorTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [categoryTab, setCategoryTab] = useState<'pastels' | 'rich' | 'elegant'>('pastels');

  const colorOptions: Record<string, { value: ColorTheme; label: string; color: string }[]> = {
    pastels: [
      { value: 'rose', label: 'Rose', color: 'bg-rose-500' },
      { value: 'lavender', label: 'Lavender', color: 'bg-purple-400' },
      { value: 'mint', label: 'Mint', color: 'bg-green-400' },
      { value: 'peach', label: 'Peach', color: 'bg-orange-400' },
      { value: 'sky', label: 'Sky', color: 'bg-sky-400' }
    ],
    rich: [
      { value: 'ruby', label: 'Ruby', color: 'bg-red-600' },
      { value: 'emerald', label: 'Emerald', color: 'bg-emerald-600' },
      { value: 'sapphire', label: 'Sapphire', color: 'bg-blue-600' },
      { value: 'amethyst', label: 'Amethyst', color: 'bg-fuchsia-600' },
      { value: 'amber', label: 'Amber', color: 'bg-amber-600' }
    ],
    elegant: [
      { value: 'burgundy', label: 'Burgundy', color: 'bg-red-800' },
      { value: 'navy', label: 'Navy', color: 'bg-blue-800' },
      { value: 'forest', label: 'Forest', color: 'bg-green-800' },
      { value: 'gold', label: 'Gold', color: 'bg-yellow-600' },
      { value: 'silver', label: 'Silver', color: 'bg-gray-500' }
    ]
  };

  return (
    <div className="relative">
      {/* Theme controls container */}
      <div className="flex items-center space-x-2">
        {/* Dark mode toggle */}
        <button
          onClick={toggleDarkMode}
          className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {darkMode ? (
            <Sun size={20} className="text-yellow-400" />
          ) : (
            <Moon size={20} className="text-gray-700" />
          )}
        </button>

        {/* Color theme selector dropdown trigger */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
          aria-label="Choose theme color"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <Sliders size={20} className="text-gray-700 dark:text-gray-300" />
          <ChevronDown size={16} className="ml-1 text-gray-500 dark:text-gray-400" />
        </button>
      </div>

      {/* Dropdown menu for color themes */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-64 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-10 border border-gray-200 dark:border-gray-700"
          >
            <div className="px-3 py-2 flex justify-between items-center border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Select Theme Color
              </span>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                aria-label="Close theme selector"
              >
                <X size={16} />
              </button>
            </div>
            
            {/* Category tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setCategoryTab('pastels')}
                className={`flex-1 py-2 text-sm font-medium text-center ${
                  categoryTab === 'pastels'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Pastels
              </button>
              <button
                onClick={() => setCategoryTab('rich')}
                className={`flex-1 py-2 text-sm font-medium text-center ${
                  categoryTab === 'rich'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Rich
              </button>
              <button
                onClick={() => setCategoryTab('elegant')}
                className={`flex-1 py-2 text-sm font-medium text-center ${
                  categoryTab === 'elegant'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Elegant
              </button>
            </div>
            
            {/* Color options for selected category */}
            <div className="mt-2 px-2 py-1 max-h-60 overflow-y-auto">
              <div className="grid grid-cols-2 gap-2">
                {colorOptions[categoryTab].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setColorTheme(option.value);
                      setIsOpen(false);
                    }}
                    className={`
                      flex items-center px-3 py-2 rounded-md text-sm 
                      ${colorTheme === option.value 
                        ? 'bg-gray-100 dark:bg-gray-700 font-medium' 
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }
                      ${darkMode ? 'text-gray-300' : 'text-gray-700'}
                      transition-colors duration-150
                    `}
                  >
                    <span
                      className={`inline-block w-5 h-5 rounded-full mr-2 ${option.color} shadow-sm border border-gray-200 dark:border-gray-600`}
                    ></span>
                    <span className="flex-grow text-left">{option.label}</span>
                    {colorTheme === option.value && (
                      <span className="ml-auto text-xs">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="mt-2 px-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Theme preferences are saved automatically.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ThemeSelector;